import { useAuth } from '../../../../hooks'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import cn from '../../../../lib/utils/cn'
import { Button, Dialog, Input, Textarea, IconButton } from '../../../ui'
import AppContext from '../../../../context/appContext'
import { useSearch, useSearchAi } from '../../../../hooks/api/documentHooks'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Cancel, ErrorOutline, Search, SendRounded } from '@mui/icons-material'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import { QuickSearchDialogItem, AskAiDialogItem } from './components'
import SearchModeSwitcher from './components/SearchModeSwitcher'
import Loader from '../../../common/Loader'
import { errors } from '../../../../lib/utils/toastCaller'
import { useMediaQuery } from 'react-responsive'
import PreviewModal from '../../../../pages/Workspace/FilePreviewPage/PreviewModal'
import { useBilling, Features } from '../../../../context/billingContext'

interface SearchModalProps {
	open: boolean
	onClose: () => void
}

const SearchModal: React.FC<SearchModalProps> = ({
	open,
	onClose,
}) => {
	const { isAuthenticated } = useAuth()
	const navigate = useNavigate()
	const location = useLocation()
	const [searchParams, setSearchParams] = useSearchParams()
	const defaultQuery = searchParams?.get('query')
	const defaultType = searchParams?.get('type') || 'quick-search'

	const { setSearchResults, isApp } = useContext(AppContext)
	const { hasFeature } = useBilling()

	const [currentTab, setCurrentTab] = useState(defaultType)
	const isMobile = useMediaQuery({ maxWidth: 768 })
	const [filePreview, setFilePreview] = useState<{
		id: string
		versionId?: string
	} | null>(null)
	const [quickQuery, setQuickQuery] = useState('')
	const [aiQuery, setAiQuery] = useState('')

	useEffect(() => {
		if (defaultQuery && open) {
			if (currentTab === 'quick-search') {
				setQuickQuery(defaultQuery)
			} else {
				setAiQuery(defaultQuery)
			}
		}
	}, [defaultQuery, open])

	const {
		data: searchData,
		refetch: refetchSearch,
		isLoading: isLoadingSearch,
		isRefetching: isRefetchingSearch,
		isFetched: isFetchedSearch,
		error: quickSearchError,
	} = useSearch(
		{ query: quickQuery?.trim()?.toLocaleLowerCase(), limit: 10 },
		{
			enabled: false,
		}
	)

	const {
		data: aiData,
		refetch: refetchSearchAi,
		isLoading: isLoadingAiSearch,
		isRefetching: isRefetchingAiSearch,
		isFetched: isFetchedAiSearch,
		error: aiSearchError,
	} = useSearchAi(
		{ query: aiQuery.trim()?.toLocaleLowerCase(), limit: 10 },
		{
			enabled: false,
		}
	)

	const handleSearch = () => {
		const query = currentTab === 'quick-search' ? quickQuery : aiQuery
		if (!query?.trim().length || !open || !isAuthenticated) return

		if (currentTab === 'quick-search') {
			refetchSearch()
		}
		if (currentTab === 'ai-search') {
			refetchSearchAi()
		}
	}

	const handleShowMore = () => {
		const query = currentTab === 'quick-search' ? quickQuery : aiQuery

		if (!query) return

		if (location?.pathname?.split('/')?.includes('search-results')) {
			setSearchParams({ query: query?.trim() })
		}

		const data = currentTab === 'quick-search' ? searchData?.data : aiData?.data
		const dataArr = data ? [...data] : []
		const lastItemId = dataArr.length ? dataArr[dataArr.length - 1]?.id : null
		setSearchResults({
			startId: lastItemId ?? null,
			data: (currentTab === 'quick-search' ? searchData?.data : aiData?.data) || [],
		})

		navigate(`/search-results?query=${query}&type=${currentTab}`)
		onClose()
	}

	const handlePreview = (
		id: string,
		versionId?: string | null,
		type?: number | null
	) => {
		if (type === 0) {

			if (isMobile || isApp) {
				hasFeature(Features.Basic) && setFilePreview({ id, versionId: versionId ?? undefined })
				return
			}

			window.history.pushState(null, '', location.pathname + location.search)
			window.open(
				`aura/preview?is_blank=true&id=${id}${versionId ? '&versionId=' + versionId : ''
				}`,
				'_blank'
			)
			onClose()
			return
		}
	}

	const handleClose = () => {
		onClose()
	}

	const isLoading =
		isLoadingSearch ||
		isRefetchingSearch ||
		isLoadingAiSearch ||
		isRefetchingAiSearch

	const error = useMemo(
		() =>
			currentTab === 'quick-search'
				? isFetchedSearch
					? quickSearchError
					: null
				: isFetchedAiSearch
					? aiSearchError
					: null,
		[currentTab, quickSearchError, aiSearchError]
	)

	return (
		<>
			<Dialog
				isOpen={open}
				onClose={handleClose}
				closeByClickOverlay={false}
				hideCloseButton
				className="w-[349px] bg-white dark:bg-background-dark rounded-lg"
			>
				<div>
					<SearchModeSwitcher
						tabs={['quick-search', 'ai-search']}
						currentTab={currentTab}
						setCurrentTab={setCurrentTab}
						onClose={handleClose}
					/>
					<div className={cn('gap-2 flex flex-col w-full space-between')}>
						{currentTab === 'ai-search' ? (
							<div
								className={cn(
									'w-full gap-3 flex flex-row space-between items-end mt-5'
								)}
							>
								<Textarea
									value={aiQuery}
									onChange={(e) => setAiQuery(e.target.value)}
									placeholder="Question"
									onEnterClick={handleSearch}
									textareaClassName={cn('h-20')}
								/>
								<IconButton
									icon={<SendRounded />}
									variant="primary"
									className="w-10 h-10 min-w-10"
									onClick={handleSearch}
									disabled={isLoadingAiSearch}
								/>
							</div>
						) : (
							<div className={cn('w-full mt-5 flex items-center gap-2')}>
								<Input
									value={quickQuery}
									onChange={(e) => setQuickQuery(e.target.value)}
									inputClassName="h-10 px-2 flex-1 pr-8"
									wrapperClassName="w-full"
									placeholder="Enter Search Text"
									onKeyUp={(e: React.KeyboardEvent) => {
										if (e.key === 'Enter') {
											handleSearch()
										}
									}}

									rightIcon={
										quickQuery?.trim()?.length ? (
											<Cancel
												className="fill-stroke-light w-4 hover:fill-stroke-light-hover cursor-pointer"
												onClick={() => setQuickQuery('')}
											/>
										) : null
									}
								/>
								<IconButton
									icon={<Search />}
									variant="primary"
									className="w-10 h-10 min-w-10"
									onClick={handleSearch}
									disabled={isLoadingSearch}
								/>
							</div>
						)}
						{isLoading ? (
							<Loader classNameWrapper="w-full mx-auto flex items-center justify-center my-5" />
						) : !!error ? (
							<div className="flex gap-2 items-center w-full h-12 justify-center bg-red-50 rounded">
								<ErrorOutline className="w-5 h-5 fill-negative" />
								<p className="text-red-500 font-bold text-sm text-center">
									{(error as any)?.response?.data?.errors?.[0]
										? errors?.[(error as any).response.data.errors[0]]?.() ||
										`${currentTab} search failed. Please try again.`
										: `${currentTab} search failed. Please try again.`}
								</p>
							</div>
						) : (
							<div className='w-full h-fit max-h-[400px] overflow-y-auto pr-1'>
								{currentTab === 'quick-search' && searchData?.data && (
									<div className="flex flex-col gap-1 w-full h-full rounded-lg p-0 m-0">
										{isFetchedSearch && !!searchData?.data.length ? (
											searchData.data
												.slice(0, 9)
												.map((item: any) => (
													<QuickSearchDialogItem
														key={item.id}
														item={item}
														onPreview={handlePreview}
													/>
												))
										) : (
											<div className="flex flex-col items-center w-full h-12 justify-center">
												<p className="text-text-secondary text-sm">
													No results found
												</p>
											</div>
										)}
									</div>
								)}

								{currentTab === 'ai-search' && isFetchedAiSearch && (
									<div className="flex flex-col gap-1 w-full h-full rounded-xl p-0 m-0 bg-[#F8F8F8] dark:bg-background-dark dark:text-text-main-dark">
										<div className="flex items-center p-2.5">
											<AutoAwesomeRoundedIcon className="text-[#FF841B] h-4 w-4" />
											<p className="text-text-main dark:text-text-main-dark font-bold text-sm ml-2.5">
												AI
											</p>
										</div>
										{aiData?.data?.length ? (
											aiData?.data
												?.slice(0, 9)
												.map((item: any) => (
													<AskAiDialogItem
														key={item.id}
														item={item}
														onPreview={handlePreview}
													/>
												))
										) : (
											<div className="flex flex-col items-center w-full h-12 justify-center">
												<p className="text-text-secondary text-sm">
													No results found
												</p>
											</div>
										)}
									</div>
								)}

								{
									!isLoading &&
									((currentTab === 'quick-search' &&
										searchData &&
										searchData?.data?.length > 9) ||
										(currentTab === 'ai-search' &&
											aiData &&
											aiData?.data?.length > 9)) && (
										<Button
											variant="secondary"
											disabled={!(currentTab === 'quick-search' ? quickQuery : aiQuery)}
											onClick={handleShowMore}
											className="w-[340px] box-border h-10 m-0.5 mt-3"
											label="Show More"
										/>
									)
								}
							</div>
						)}
					</div>
				</div>
			</Dialog>
			<PreviewModal
				isOpen={Boolean(filePreview && hasFeature(Features.Basic))}
				onClose={() => setFilePreview(null)}
				fileId={filePreview?.id ?? ''}
				versionId={filePreview?.versionId ?? ''}
			/>
		</>
	)
}

export default SearchModal
